import { verboseDebug } from 'classes/helpers/debug.helper';
import { AimingContext } from 'contexts/aiming.context';
import { MachineContext } from 'contexts/machine.context';
import { ResetPlateMode } from 'enums/machine.enums';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import useLatest from 'hooks/useLatestRef';
import { IVerboseComponent } from 'interfaces/i-components';
import { ISendBP } from 'interfaces/i-pitch-aiming';
import { FF_NAME_ON_DEMAND_MODE } from 'lib_ts/enums/training.enums';
import { useContext, useEffect } from 'react';
import useHittersStore from 'stores/use-hitters-store';
import useListLibraryStore from 'stores/use-list-library-store';
import { useOnDemandStore } from 'stores/use-on-demand-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

// automatically loads shots whenever the relevant shotIDs changes on the TMS record
const useSyncShotListener = (props: IVerboseComponent) => {
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);

  const { machine, lastMS } = useContext(MachineContext);

  const latestPreview = useLatest(machine.enable_pitch_preview);
  const latestMsHash = useLatest(lastMS?.matching_hash);

  const { isTraining, pitchBP, setAndSendForBP } = useContext(AimingContext);
  const latestPitchBP = useLatest(pitchBP);
  const latestSSBP = useLatest(setAndSendForBP);
  const latestIsTraining = useLatest(isTraining);

  const tms = useTrainedMsStore(
    (s) => s.tmsDict[pitchBP?._mx_tms?.bs_hash ?? '']
  );
  const odms = useOnDemandStore(
    (s) => s.odmsDict[pitchBP?._mx_tms?.bs_hash ?? '']
  );

  // after TMS/ODMS changes, shotIDs will be different
  const shotIDs = onDemand ? odms?.shotIDs : tms?.shotIDs;

  useEffect(() => {
    const callback = async () => {
      if (latestIsTraining.current) {
        // skip everything while training
        return;
      }

      const bs_hash = latestPitchBP.current?._mx_tms?.bs_hash ?? '';

      if (!bs_hash) {
        // nothing is selected
        return;
      }

      const shouldSetPitchBP =
        latestMsHash.current &&
        latestMsHash.current ===
          latestPitchBP.current?._mx_tms?.ms.matching_hash;

      if (!shouldSetPitchBP) {
        verboseDebug(props, 'skipped setPitchBP');
        return;
      }

      // resend the pitch using the latest shots
      const sendConfig: ISendBP = {
        skipFiringCheck: true,
        skipPreview: !latestPreview.current,
        trigger: `${props.identifier} > shots changed`,
        list: useListLibraryStore.getState().activeList,
        hitter: useHittersStore.getState().activeHitter,
      };

      await latestSSBP.current({
        pitch: latestPitchBP.current,
        resetPlate: ResetPlateMode.Skip,
        sendConfig: sendConfig,
      });

      verboseDebug(props, 'triggered setPitchBP w/ auto-send', sendConfig);
    };

    callback();
  }, [
    shotIDs, // new shots were loaded
    onDemand, // entered/exited on-demand
  ]);
};

export default useSyncShotListener;
